import * as React from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import {
  type ColumnDef,
  type RowSelectionState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { DataTableLabels } from '@repo/ui-contracts';
import { Button } from './button';
import { Checkbox } from './checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { cn } from '../lib/utils';

type Alignment = 'left' | 'center' | 'right';

type DataTableSortDirection = 'asc' | 'desc';

type DataTableSortState = {
  key: string;
  direction: DataTableSortDirection;
} | null;

type DataTablePageSizeOption = number;
type DataTablePageSizePlacement = 'left' | 'right';
type DataTableRowVariant = 'cards' | 'separated' | 'plain';
type DataTableDensity = 'comfortable' | 'compact';

type DataTableColumn<TData> = {
  id: string;
  header: React.ReactNode;
  cell: (row: TData) => React.ReactNode;
  sortable?: boolean;
  align?: Alignment;
  headerClassName?: string;
  cellClassName?: string;
};

type DataTableTestIds<TData> = {
  table?: string;
  row?: (row: TData) => string | undefined;
  sortTrigger?: (columnId: string) => string | undefined;
  selectAll?: string;
  selectRow?: (row: TData) => string | undefined;
  pageSize?: string;
  summary?: string;
  previous?: string;
  pageIndicator?: string;
  next?: string;
};

type DataTableProps<TData> = {
  rows: TData[];
  columns: DataTableColumn<TData>[];
  getRowId: (row: TData) => string;
  labels: DataTableLabels<TData>;
  page: number;
  totalPages: number;
  pageSize: number;
  pageSizePlacement?: DataTablePageSizePlacement;
  pageSizeOptions: readonly DataTablePageSizeOption[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  summary?: React.ReactNode;
  selectable?: boolean;
  selectedRowIds?: string[];
  onSelectedRowIdsChange?: (rowIds: string[]) => void;
  sort?: DataTableSortState;
  onSortChange?: (sort: DataTableSortState) => void;
  isLoading?: boolean;
  rowVariant?: DataTableRowVariant;
  density?: DataTableDensity;
  testIds?: DataTableTestIds<TData>;
};

const resolveUpdater = <T,>(updater: T | ((previous: T) => T), previous: T): T => {
  if (typeof updater === 'function') {
    return (updater as (previous: T) => T)(previous);
  }
  return updater;
};

const toAlignmentClass = (alignment: Alignment | undefined) => {
  if (alignment === 'right') {
    return 'text-right';
  }
  if (alignment === 'center') {
    return 'text-center';
  }
  return 'text-left';
};

function DataTable<TData>({
  rows,
  columns,
  getRowId,
  labels,
  page,
  totalPages,
  pageSize,
  pageSizePlacement = 'left',
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  summary,
  selectable = false,
  selectedRowIds: controlledSelectedRowIds,
  onSelectedRowIdsChange,
  sort: controlledSort,
  onSortChange,
  isLoading = false,
  rowVariant = 'cards',
  density = 'comfortable',
  testIds,
}: DataTableProps<TData>) {
  const [localSelectedRowIds, setLocalSelectedRowIds] = React.useState<string[]>([]);
  const [localSort, setLocalSort] = React.useState<DataTableSortState>(null);

  const selectedRowIds = controlledSelectedRowIds ?? localSelectedRowIds;
  const setSelectedRowIds = onSelectedRowIdsChange ?? setLocalSelectedRowIds;
  const sort = controlledSort ?? localSort;
  const setSort = onSortChange ?? setLocalSort;

  const sorting = React.useMemo<SortingState>(
    () => (sort ? [{ id: sort.key, desc: sort.direction === 'desc' }] : []),
    [sort],
  );
  const rowSelection = React.useMemo<RowSelectionState>(() => {
    const next: RowSelectionState = {};
    for (const rowId of selectedRowIds) {
      next[rowId] = true;
    }
    return next;
  }, [selectedRowIds]);

  const mappedColumns = React.useMemo<ColumnDef<TData>[]>(
    () =>
      columns.map((columnDef) => ({
        id: columnDef.id,
        enableSorting: Boolean(columnDef.sortable),
        header: ({ column }) => {
          const sortValue = column.getIsSorted();
          if (!columnDef.sortable) {
            return (
              <span
                className={cn(toAlignmentClass(columnDef.align), columnDef.headerClassName)}
                data-testid={testIds?.sortTrigger?.(columnDef.id)}
              >
                {columnDef.header}
              </span>
            );
          }

          return (
            <button
              type="button"
              className={cn(
                'inline-flex items-center gap-1 type-overline uppercase text-muted-foreground transition-colors hover:text-foreground',
                toAlignmentClass(columnDef.align),
                columnDef.headerClassName,
              )}
              data-testid={testIds?.sortTrigger?.(columnDef.id)}
              onClick={() => {
                column.toggleSorting(column.getIsSorted() === 'asc');
              }}
            >
              <span>{columnDef.header}</span>
              {sortValue === 'asc' ? <ArrowUp className="size-icon-xs" /> : null}
              {sortValue === 'desc' ? <ArrowDown className="size-icon-xs" /> : null}
              {sortValue === false ? <ArrowUpDown className="size-icon-xs" /> : null}
            </button>
          );
        },
        cell: ({ row }) => (
          <div className={cn(toAlignmentClass(columnDef.align), columnDef.cellClassName)}>
            {columnDef.cell(row.original)}
          </div>
        ),
      })),
    [columns, testIds],
  );

  const table = useReactTable({
    data: rows,
    columns: selectable
      ? [
          {
            id: '__select',
            enableSorting: false,
            header: ({ table }) => (
              <Checkbox
                aria-label={labels.selectAllRows}
                data-testid={testIds?.selectAll}
                checked={
                  table.getIsAllPageRowsSelected()
                    ? true
                    : table.getIsSomePageRowsSelected()
                      ? 'indeterminate'
                      : false
                }
                onCheckedChange={(checked) => table.toggleAllPageRowsSelected(checked === true)}
              />
            ),
            cell: ({ row }) => (
              <Checkbox
                aria-label={labels.selectRow(row.original)}
                data-testid={testIds?.selectRow?.(row.original)}
                checked={row.getIsSelected()}
                onCheckedChange={(checked) => row.toggleSelected(checked === true)}
              />
            ),
          },
          ...mappedColumns,
        ]
      : mappedColumns,
    state: {
      sorting,
      rowSelection,
    },
    getRowId: (row) => getRowId(row),
    enableRowSelection: selectable,
    enableSortingRemoval: false,
    manualSorting: true,
    onRowSelectionChange: (updater) => {
      const next = resolveUpdater(updater, rowSelection);
      const nextIds = Object.entries(next)
        .filter((entry) => entry[1] === true)
        .map((entry) => entry[0]);
      setSelectedRowIds(nextIds);
    },
    onSortingChange: (updater) => {
      const next = resolveUpdater(updater, sorting);
      const nextItem = next[0];
      if (!nextItem) {
        setSort(null);
        return;
      }
      setSort({
        key: nextItem.id,
        direction: nextItem.desc ? 'desc' : 'asc',
      });
    },
    getCoreRowModel: getCoreRowModel(),
  });

  const pageIndicator = labels.pageIndicator(page, totalPages);
  const tableClassName =
    rowVariant === 'cards'
      ? 'w-full border-separate border-spacing-y-2 type-body-sm'
      : 'w-full border-collapse type-body-sm';
  const rowClassName =
    rowVariant === 'separated'
      ? 'border-b border-border text-foreground last:border-b-0'
      : rowVariant === 'plain'
        ? 'text-foreground'
        : 'rounded-lg border bg-card text-foreground shadow-sm';
  const cellPaddingClass = density === 'compact' ? 'px-control-x-sm py-control-y-sm align-middle' : 'px-control-x-sm py-3 align-middle';
  const pageSizeControl = (
    <div data-testid={testIds?.pageSize} className="flex items-center gap-2 type-body-sm">
      <span className="text-muted-foreground">{labels.pageSize}</span>
      <Select
        value={String(pageSize)}
        onValueChange={(value) => {
          const next = Number.parseInt(value, 10);
          if (!Number.isNaN(next)) {
            onPageSizeChange(next);
          }
        }}
      >
        <SelectTrigger className="h-control-base w-20">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {pageSizeOptions.map((option) => (
            <SelectItem key={option} value={String(option)}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
  const summaryControl = summary ? (
    <div data-testid={testIds?.summary} className="text-muted-foreground">
      {summary}
    </div>
  ) : null;
  const hasLeftControls = pageSizePlacement === 'left' || summaryControl !== null;

  return (
    <div className="space-y-4">
      {!isLoading && table.getRowModel().rows.length > 0 ? (
        <div className="overflow-x-auto">
          <table data-testid={testIds?.table} className={tableClassName}>
            <thead className="type-overline uppercase text-muted-foreground">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={cn(
                        'px-control-x-sm py-control-y-sm',
                        header.id === '__select' ? 'text-left' : '',
                        header.id === 'actions' ? 'text-right' : '',
                      )}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={rowClassName}
                  data-testid={testIds?.row?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={cn(
                        cellPaddingClass,
                        cell.column.id === 'actions' ? 'text-right' : '',
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {!isLoading && table.getRowModel().rows.length === 0 ? (
        <div
          className="rounded-lg border border-dashed border-muted-foreground/40 px-6 py-10 text-center"
        >
          <h3 className="font-display text-lg font-semibold">{labels.emptyTitle}</h3>
          <p className="mt-2 type-body-sm text-muted-foreground">{labels.emptyDescription}</p>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3 type-body-sm">
        {hasLeftControls ? (
          <div className="flex flex-wrap items-center gap-4">
            {pageSizePlacement === 'left' ? pageSizeControl : null}
            {summaryControl}
          </div>
        ) : null}
        <div className={cn('flex flex-wrap items-center gap-2', hasLeftControls ? '' : 'ml-auto')}>
          {pageSizePlacement === 'right' ? pageSizeControl : null}
          <Button
            type="button"
            data-testid={testIds?.previous}
            variant="outline"
            size="sm"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
          >
            {labels.previous}
          </Button>
          <span data-testid={testIds?.pageIndicator} className="text-xs text-muted-foreground">
            {pageIndicator}
          </span>
          <Button
            type="button"
            data-testid={testIds?.next}
            variant="outline"
            size="sm"
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
          >
            {labels.next}
          </Button>
        </div>
      </div>
    </div>
  );
}

export { DataTable };
export type {
  DataTableColumn,
  DataTableLabels,
  DataTablePageSizeOption,
  DataTablePageSizePlacement,
  DataTableProps,
  DataTableSortDirection,
  DataTableSortState,
  DataTableTestIds,
  DataTableDensity,
  DataTableRowVariant,
};
